export type CollabStatus =
  | "offer_pending"
  | "negotiating"
  | "awaiting_payment"
  | "in_progress"
  | "draft_submitted"
  | "revision_requested"
  | "draft_approved"
  | "published"
  | "live_period"
  | "completed"
  | "cancelled"
  | "expired";

export type Party = "creator" | "brand" | "platform" | "none";

export type MilestoneTrigger =
  "on_accept" | "on_draft_approval" | "on_publish" | "on_live_period_end";

/**
 * The only legal edges of the collab lifecycle. Anything not listed here is a bug,
 * and `assertTransition` will refuse it rather than let the collab drift into a
 * state the payment logic was never written for.
 *
 * Payments happen off-platform, so a tranche falling due mid-collab does not move
 * the collab itself — it only flips a milestone to `due` and the pending party to
 * the payer. `awaiting_payment` is used once, for the advance that gates the
 * start of work.
 */
const ALLOWED_TRANSITIONS: Record<CollabStatus, readonly CollabStatus[]> = {
  offer_pending: ["negotiating", "awaiting_payment", "in_progress", "expired", "cancelled"],
  negotiating: ["negotiating", "awaiting_payment", "in_progress", "expired", "cancelled"],
  awaiting_payment: ["in_progress", "expired", "cancelled"],
  in_progress: ["draft_submitted", "cancelled"],
  draft_submitted: ["draft_approved", "revision_requested", "cancelled"],
  revision_requested: ["draft_submitted", "cancelled"],
  draft_approved: ["published", "cancelled"],
  published: ["live_period", "completed"],
  live_period: ["completed"],
  completed: [],
  cancelled: [],
  expired: [],
};

/**
 * Whose move it is once a collab reaches a given status. `negotiating` is the one
 * exception — it depends on who sent the last counter — so callers pass it in.
 */
export const PENDING_PARTY_BY_STATUS: Record<CollabStatus, Party> = {
  offer_pending: "creator",
  negotiating: "creator",
  awaiting_payment: "brand",
  in_progress: "creator",
  draft_submitted: "brand",
  revision_requested: "creator",
  draft_approved: "creator",
  published: "brand",
  live_period: "none",
  completed: "none",
  cancelled: "none",
  expired: "none",
};

export function canTransition(from: CollabStatus, to: CollabStatus): boolean {
  return ALLOWED_TRANSITIONS[from].includes(to);
}

export class IllegalTransitionError extends Error {
  constructor(
    readonly from: CollabStatus,
    readonly to: CollabStatus,
  ) {
    super(`Illegal collab transition: ${from} -> ${to}`);
    this.name = "IllegalTransitionError";
  }
}

export function assertTransition(from: CollabStatus, to: CollabStatus): void {
  if (!canTransition(from, to)) throw new IllegalTransitionError(from, to);
}

export const TERMINAL_STATUSES: readonly CollabStatus[] = ["completed", "cancelled", "expired"];

export function isTerminal(status: CollabStatus): boolean {
  return TERMINAL_STATUSES.includes(status);
}

/** A milestone the payer still owes, or that is waiting on the creator's receipt. */
export const OPEN_MILESTONE_STATUSES = ["pending", "due", "paid"] as const;

/**
 * Once the post is live the collaboration is done from everyone's point of view.
 * Any keep-up window or final tranche is handled through payments, not a countdown.
 */
export function effectiveCollabStatus(
  status: CollabStatus,
  _milestones: { status: string }[],
): CollabStatus {
  if (status === "live_period") return "completed";
  return status;
}

/** List rows mirror the room: a live post reads as completed, not a ticking clock. */
export function effectiveListStatus(
  status: CollabStatus,
  _openMilestoneCount: number,
): CollabStatus {
  if (status === "live_period") return "completed";
  return status;
}

export function listTimerForStatus(
  status: CollabStatus,
  input: {
    draftDeadline: Date | null;
    paymentDeadline: Date | null;
    liveUntil: Date | null;
  },
  now: Date,
  formatRemaining: (deadline: Date, now: Date) => string | null,
): { timerDeadline: Date | null; timerLabel: string | null } {
  if (isTerminal(status)) {
    return { timerDeadline: null, timerLabel: null };
  }

  let deadline: Date | null = null;
  switch (status) {
    case "offer_pending":
    case "negotiating":
    case "awaiting_payment":
      deadline = input.paymentDeadline;
      break;
    case "in_progress":
    case "revision_requested":
    case "draft_submitted":
    case "draft_approved":
    case "published":
      deadline = input.draftDeadline ?? input.paymentDeadline;
      break;
    default:
      deadline = null;
  }

  if (!deadline) return { timerDeadline: null, timerLabel: null };
  return { timerDeadline: deadline, timerLabel: formatRemaining(deadline, now) };
}

/** Collabs that are under way, where tranches can fall due. */
export const ACTIVE_STATUSES: readonly CollabStatus[] = [
  "in_progress",
  "draft_submitted",
  "revision_requested",
  "draft_approved",
  "published",
  "live_period",
];

/**
 * One-line status text, written from the reader's point of view.
 *
 * `paymentDue` outranks the work state on purpose. A tranche falling due does
 * not move the collab — the creator may still have a post to publish — but from
 * either side's point of view the collab is waiting on money, and saying
 * "Approved — ready to publish" while ₹6,250 sits unpaid buries the thing that
 * actually needs doing.
 */
export function statusLine(
  status: CollabStatus,
  pendingParty: Party,
  viewer: "creator" | "brand",
  paymentDue = false,
) {
  const waitingOnYou = pendingParty === viewer;

  if (paymentDue && status !== "cancelled" && status !== "expired") {
    return viewer === "brand" ? "Payment due — pay the creator" : "Waiting for payment";
  }

  switch (status) {
    case "offer_pending":
      return waitingOnYou ? "New offer needs your response" : "Waiting for the creator to respond";
    case "negotiating":
      return waitingOnYou ? "Counter-offer needs your response" : "Waiting on the other side";
    case "awaiting_payment":
      return viewer === "brand"
        ? "Send the advance to start work"
        : "Waiting for the collaborator to send the advance";
    case "in_progress":
      return viewer === "creator"
        ? "Work under way — draft due"
        : "Creator is working on the draft";
    case "draft_submitted":
      return viewer === "brand" ? "Draft needs your review" : "Draft submitted, awaiting review";
    case "revision_requested":
      return viewer === "creator" ? "Revision requested" : "Waiting on the revised draft";
    case "draft_approved":
      return viewer === "creator" ? "Approved — ready to publish" : "Approved, awaiting the post";
    case "published":
      return viewer === "brand"
        ? "Confirm the post is live"
        : "Awaiting the collaborator's confirmation";
    case "live_period":
      return viewer === "creator"
        ? "Post is live — keep it up until the period ends"
        : "Post is live — collab delivered";
    case "completed":
      return "";
    case "cancelled":
      return "Cancelled";
    case "expired":
      return "Offer expired";
  }
}

export type PillTone = "neutral" | "amber" | "blue" | "green" | "red";

export function statusPill(
  status: CollabStatus,
  paymentDue = false,
): { label: string; tone: PillTone } {
  if (paymentDue && status !== "cancelled" && status !== "expired") {
    return { label: "Awaiting payment", tone: "amber" };
  }

  switch (status) {
    // An offer nobody has answered yet is a different thing from one being
    // haggled over: the first is waiting, the second is moving.
    case "offer_pending":
      return { label: "Pending", tone: "amber" };
    case "negotiating":
      return { label: "Negotiating", tone: "amber" };
    case "awaiting_payment":
      return { label: "Awaiting payment", tone: "amber" };
    case "in_progress":
    case "revision_requested":
      return { label: "In progress", tone: "blue" };
    case "draft_submitted":
    case "draft_approved":
    case "published":
      return { label: "Delivered", tone: "blue" };
    case "live_period":
      return { label: "Post live", tone: "green" };
    case "completed":
      return { label: "Completed", tone: "green" };
    case "cancelled":
      return { label: "Cancelled", tone: "neutral" };
    case "expired":
      return { label: "Expired", tone: "neutral" };
  }
}
